/**
 * SSoT — Référentiel des catégories d'âge joueur.
 *
 * Consomme GET /api/meta/player-categories (backend `app/core/constants.py`)
 * avec un cache mémoire au niveau module : un seul fetch par session, partagé
 * entre tous les dropdowns. Le hook `useCategoriesOptions()` est l'unique
 * point de consommation côté UI — aucune liste hardcodée locale autorisée.
 *
 * Le fallback ci-dessous est volontaire (réseau down / endpoint absent en
 * dev) — il DOIT rester synchronisé avec PLAYER_CATEGORIES dans
 * app/core/constants.py côté backend. Si tu modifies cette liste, modifie
 * aussi le backend (et vice-versa).
 */
import { useEffect, useState } from 'react'
import api from '../services/api'

const FALLBACK = ['U14', 'U15', 'U16', 'U17', 'U18', 'U19', 'Seniors']

let cache = null      // liste résolue (string[]), null tant que jamais chargée
let pending = null    // Promise<string[]> en vol, partagée entre appelants

/**
 * Renvoie la liste des catégories. Cache mémoire — un seul fetch par session.
 * En cas d'échec réseau, renvoie le fallback hardcodé (jamais reject).
 */
export async function getPlayerCategories() {
  if (cache) return cache
  if (pending) return pending
  pending = api
    .get('/meta/player-categories')
    .then(res => {
      const list = Array.isArray(res.data?.categories) ? res.data.categories : FALLBACK
      cache = list
      return list
    })
    .catch(() => {
      // Pas de cache sur erreur : on retentera au prochain appel.
      return FALLBACK
    })
    .finally(() => {
      pending = null
    })
  return pending
}

/**
 * Hook React. Retourne `{ categories, loading, error }`.
 * Si le cache est déjà chaud, `loading=false` dès le premier render (pas de flash).
 */
export function useCategoriesOptions() {
  const [categories, setCategories] = useState(cache || [])
  const [loading, setLoading] = useState(!cache)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (cache) return
    let cancelled = false
    getPlayerCategories()
      .then(list => {
        if (cancelled) return
        setCategories(list)
        setLoading(false)
      })
      .catch(err => {
        if (cancelled) return
        setError(err)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { categories, loading, error }
}
